/**
 * Guest checkout money math — the single implementation, shared by the browser
 * hubs and the Cloudflare Pages checkout function.
 *
 * Why shared and not mirrored: `/api/service-requests/checkout` accepts whatever
 * `amountCents` the client sends (bounded only by $1…$500k), so a crafted
 * request can name its own price. The server needs to recompute the quote to
 * catch that — but a second implementation would drift from the client by a
 * cent and start failing honest orders. So the formulas and the rate constants
 * live here once, and both sides call them.
 *
 * Inputs are coordinates the customer already committed to (vendor pick, drop
 * pin, pickup pin). Lying about them makes the job undeliverable, which is the
 * property that makes a coordinate-based recompute worth trusting.
 */

import { PARTNER_OPERATING_CITIES } from './partnerServiceArea';
import { estimateSemiWideQuote } from './semiWideLoadQuote';

/** Great-circle miles. Must stay identical to waterDeliveryService.milesBetween. */
export function haversineMiles(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
): number | null {
  if (![lat1, lng1, lat2, lng2].every((n) => Number.isFinite(n))) return null;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const R = 3958.7613; // Earth radius miles
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const mi = R * c;
  return Number.isFinite(mi) ? mi : null;
}

function finiteCoord(v: number | null | undefined): number | null {
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

// ─── Store-pickup lines (grocery + food) ─────────────────────────────────────
// The customer buys from the vendor; we price the run vendor → drop.

export const GROCERY_PRICING = {
  cityLimitMiles: 5,
  localFlatUsd: 20,
  perMileUsd: 3.75,
  minUsd: 20,
  maxUsd: 2500,
} as const;

export const GROCERY_TIP_PRESETS_USD = [0, 3, 5, 8] as const;
export const GROCERY_TIP_MAX_USD = 100;

export type GroceryRequestQuote = {
  isLocalZone: boolean;
  storeToDropMiles: number;
  haulUsd: number;
  /** Set only for local — far grocery is billed when a driver accepts. */
  payableNowUsd: number | null;
  priceAtAccept: boolean;
};

export type GroceryAcceptQuote = {
  isLocalZone: boolean;
  storeToDropMiles: number;
  driverToStoreMiles: number | null;
  haulUsd: number;
  deadheadUsd: number;
  deliveryUsd: number;
};

function groceryClampUsd(usd: number): number {
  const n = Math.round(usd * 100) / 100;
  return Math.min(GROCERY_PRICING.maxUsd, Math.max(GROCERY_PRICING.minUsd, n));
}

function groceryHaulUsd(storeToDropMiles: number): number {
  return Math.round(storeToDropMiles * GROCERY_PRICING.perMileUsd * 100) / 100;
}

/** Customer-facing grocery quote before a driver accepts. Local is payable now; farther waits. */
export function groceryRequestQuote(storeToDropMiles: number): GroceryRequestQuote {
  const miles = Number.isFinite(storeToDropMiles) ? Math.max(0, storeToDropMiles) : 0;
  const isLocalZone = miles <= GROCERY_PRICING.cityLimitMiles;
  if (isLocalZone) {
    return {
      isLocalZone: true,
      storeToDropMiles: miles,
      haulUsd: GROCERY_PRICING.localFlatUsd,
      payableNowUsd: GROCERY_PRICING.localFlatUsd,
      priceAtAccept: false,
    };
  }
  const haulUsd = groceryHaulUsd(miles);
  return {
    isLocalZone: false,
    storeToDropMiles: miles,
    haulUsd,
    payableNowUsd: null,
    priceAtAccept: true,
  };
}

/**
 * Final grocery bill when a driver accepts.
 * Store → drop + that driver's extra miles to the store (not a fake round-trip).
 * Missing driver GPS adds $0 deadhead — never invents store→drop × 2.
 */
export function groceryAcceptQuote(
  storeToDropMiles: number,
  driverToStoreMiles?: number | null,
): GroceryAcceptQuote {
  const req = groceryRequestQuote(storeToDropMiles);
  if (req.isLocalZone) {
    return {
      isLocalZone: true,
      storeToDropMiles: req.storeToDropMiles,
      driverToStoreMiles: driverToStoreMiles ?? null,
      haulUsd: req.haulUsd,
      deadheadUsd: 0,
      deliveryUsd: req.haulUsd,
    };
  }
  const deadheadMiles =
    driverToStoreMiles != null && Number.isFinite(driverToStoreMiles) && driverToStoreMiles > 0
      ? driverToStoreMiles
      : 0;
  const deadheadUsd = groceryHaulUsd(deadheadMiles);
  return {
    isLocalZone: false,
    storeToDropMiles: req.storeToDropMiles,
    driverToStoreMiles: deadheadMiles > 0 ? deadheadMiles : (driverToStoreMiles ?? null),
    haulUsd: req.haulUsd,
    deadheadUsd,
    deliveryUsd: groceryClampUsd(req.haulUsd + deadheadUsd),
  };
}

export function groceryTipUsd(raw: unknown): number {
  const n = Math.round(Number(raw) * 100) / 100;
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.min(GROCERY_TIP_MAX_USD, n);
}

export const FOOD_PRICING = {
  cityLimitMiles: 5,
  localFlatUsd: 35,
  perMileUsd: 3.75,
  minUsd: 35,
  maxUsd: 1500,
} as const;

export type StorePickupPricing = {
  cityLimitMiles: number;
  localFlatUsd: number;
  perMileUsd: number;
  minUsd: number;
  maxUsd: number;
};

/**
 * Inside the city radius → flat fee. Beyond → per-mile round-trip, floored at
 * the flat and capped. Mirrors the receipt the customer saw.
 */
export function storePickupDeliveryUsd(
  oneWayMiles: number | null | undefined,
  pricing: StorePickupPricing,
): number | null {
  if (oneWayMiles == null || !Number.isFinite(oneWayMiles)) return null;
  if (oneWayMiles <= pricing.cityLimitMiles) return pricing.localFlatUsd;
  const mileageUsd = Math.round(oneWayMiles * 2 * pricing.perMileUsd * 100) / 100;
  const withFloor = Math.max(pricing.minUsd, mileageUsd);
  return Math.min(pricing.maxUsd, withFloor);
}

// ─── Hotshots ────────────────────────────────────────────────────────────────

export const HOTSHOT_PRICING = {
  /**
   * Long-haul floor. This exists to cover DEADHEAD: a pad run means driving 40
   * miles out empty and 40 back, so the billed pickup→drop miles understate the
   * job badly. It has nothing to say about a run that starts and ends inside a
   * town we already serve.
   */
  minTripUsd: 300,
  /**
   * In-town floor. Both ends inside the same service town means no deadhead
   * worth pricing — the truck is already here. Applying the pad floor to a
   * 5-mile Home Depot run quoted $375 for ~45 minutes of work, which is not a
   * price, it is a decline.
   */
  localMinTripUsd: 125,
  /** How far from a town center still counts as "in town". */
  localRadiusMiles: 20,
  /**
   * Straight line → road.
   *
   * Every distance here is great-circle, because that is what two pins give
   * us. A truck does not fly. Permian highway routing runs about 1.20× the
   * straight line and caliche lease roads are worse, so billing the great
   * circle quietly paid a driver for four miles out of every five they drove —
   * a systematic shortfall on every run, invisible because the quote and the
   * board both used the same understated number.
   */
  roadFactor: 1.25,
  maxQuoteUsd: 8000,
  goosenecKAdderUsd: 125,
  heavyAdderUsd: 250,
  ratePerMileUsd: {
    standard: 2.25,
    rush: 2.75,
    critical: 3.5,
  },
} as const;

/**
 * Is this run entirely inside one of the towns we already operate in?
 *
 * BOTH ends must be within the radius. Pickup-only would give town pricing to a
 * Home Depot → pad-60-miles-out haul, and route-length-only would give it to a
 * short pad-to-pad hop the driver deadheads an hour to reach. The floor is
 * about deadhead, so the test has to be about where the work sits, not how far
 * the load moves.
 *
 * Note both ends need not be the SAME town — Hobbs → Eunice is 20 miles of
 * populated corridor we cover either way.
 */
export function hotshotIsLocalRun(input: {
  pickupLat?: number | null;
  pickupLng?: number | null;
  dropLat?: number | null;
  dropLng?: number | null;
}): boolean {
  const pLat = finiteCoord(input.pickupLat);
  const pLng = finiteCoord(input.pickupLng);
  const dLat = finiteCoord(input.dropLat);
  const dLng = finiteCoord(input.dropLng);
  if (pLat == null || pLng == null || dLat == null || dLng == null) return false;
  const nearTown = (lat: number, lng: number) =>
    PARTNER_OPERATING_CITIES.some((c) => {
      const mi = haversineMiles(lat, lng, c.lat, c.lng);
      return mi != null && mi <= HOTSHOT_PRICING.localRadiusMiles;
    });
  return nearTown(pLat, pLng) && nearTown(dLat, dLng);
}

/** The floor that applies to this run. */
export function hotshotMinTripUsd(localRun: boolean): number {
  return localRun ? HOTSHOT_PRICING.localMinTripUsd : HOTSHOT_PRICING.minTripUsd;
}

/**
 * The miles we bill — and, to the nearest thing two pins can tell us, the
 * miles the truck actually turns.
 *
 * Route out and back, scaled from great-circle to road. ONE definition, because
 * the quote and the driver's advertised $/mile have to be the same number: the
 * board used to divide net pay by the one-way straight line, which advertised
 * $3.83/mile on a run that really cleared $1.53.
 */
export function hotshotBilledMiles(rawRouteMiles: number | null | undefined): number | null {
  const raw = Number(rawRouteMiles);
  if (!Number.isFinite(raw) || raw < 0) return null;
  const routeMiles = Math.round(raw * 10) / 10;
  return Math.round(routeMiles * 2 * HOTSHOT_PRICING.roadFactor * 10) / 10;
}

export type HotshotUrgencyId = keyof typeof HOTSHOT_PRICING.ratePerMileUsd;
export type HotshotLoadClassId = 'light_deck' | 'gooseneck' | 'heavy_confirm';

export function hotshotEquipmentAdderUsd(loadClass: string | null | undefined): number {
  if (loadClass === 'gooseneck') return HOTSHOT_PRICING.goosenecKAdderUsd;
  if (loadClass === 'heavy_confirm') return HOTSHOT_PRICING.heavyAdderUsd;
  return 0;
}

export function hotshotRatePerMileUsd(urgency: string | null | undefined): number {
  const key = String(urgency || 'standard') as HotshotUrgencyId;
  return HOTSHOT_PRICING.ratePerMileUsd[key] ?? HOTSHOT_PRICING.ratePerMileUsd.standard;
}

/**
 * Billable route is pickup → drop, plus an optional GPS → pickup first leg.
 *
 * Takes RAW (unrounded) leg miles: the total route is rounded to 0.1 once, on
 * the SUM, and only then billed. Rounding each leg first shifts the total by up
 * to 0.1 mi — which is a real price difference, so the argument names insist.
 *
 * EVERY RUN BILLS THE RETURN LEG. There is no such thing as a load that only
 * moves the truck one way — it has to come back, and nobody else is paying for
 * those miles.
 *
 * This used to bill one way outside town, on the theory that the $300 floor
 * covered the empty return. It does, but only while the floor is winning. Past
 * 66.7 miles one way ($300 ÷ 2 ÷ $2.25) the meter takes over, and one-way
 * billing then pays HALF the posted rate per mile actually driven — flat,
 * forever, getting no better with distance. Measured: a 133-mile pad run
 * cleared the driver $0.94 per driven mile after the platform cut and Stripe,
 * and a 200-mile run cleared the same $0.94. Fuel, tires, insurance and
 * depreciation on a 1-ton dually run about $0.85–$1.00 a mile, so those loads
 * paid nothing for the driver's day and sometimes cost them money. The
 * in-town runs, which need the least truck, were the best-paying work on the
 * board at $1.61–$2.12. Exactly backwards.
 *
 * Charging the miles the truck actually drives keeps short runs cheap (a
 * 5-mile Home Depot run is still floored at $75), leaves every short pad run
 * untouched (the $300 floor still wins below ~67 miles), and makes the long
 * hauls honest — with no cliff anywhere in between.
 */
/**
 * A rate SOURCE — the knobs a hauler can turn. The platform is one of these
 * (PLATFORM_HOTSHOT_RATES); a hauler's saved HotshotRateCard is a structural
 * superset, so it satisfies this directly. Route physics (roadFactor, ×2
 * round-trip, the maxQuote cap) are NOT here — they stay platform-global.
 */
export interface HotshotRates {
  perMile: { standard: number; rush: number; critical: number };
  minTripUsd: number;
  localMinTripUsd: number;
  gooseneckAdderUsd: number;
  heavyAdderUsd: number;
  /** Temporary sale, 0–50%. Platform passes 0. */
  promoPercentOff: number;
}

export const PLATFORM_HOTSHOT_RATES: HotshotRates = {
  perMile: { ...HOTSHOT_PRICING.ratePerMileUsd },
  minTripUsd: HOTSHOT_PRICING.minTripUsd,
  localMinTripUsd: HOTSHOT_PRICING.localMinTripUsd,
  gooseneckAdderUsd: HOTSHOT_PRICING.goosenecKAdderUsd,
  heavyAdderUsd: HOTSHOT_PRICING.heavyAdderUsd,
  promoPercentOff: 0,
};

function ratePerMileFromRates(rates: HotshotRates, urgency: string | null | undefined): number {
  const key = String(urgency || 'standard') as HotshotUrgencyId;
  return rates.perMile[key] ?? rates.perMile.standard;
}

function equipmentFromRates(rates: HotshotRates, loadClass: string | null | undefined): number {
  if (loadClass === 'gooseneck') return rates.gooseneckAdderUsd;
  if (loadClass === 'heavy_confirm') return rates.heavyAdderUsd;
  return 0;
}

/**
 * The ONE money kernel. Client quote, server checkout, and the hauler-desk
 * preview all call this so preview == charge == audit. With PLATFORM_HOTSHOT_RATES
 * (promo 0) it is byte-identical to the previous hotshotTripUsd.
 *
 * Promo policy: it discounts the MILEAGE, never below the trip floor — a sale
 * can shave a long haul but cannot price a run under its deadhead floor.
 */
export function hotshotTripUsdWithRates(
  input: {
    rawOriginToPickupMiles?: number | null;
    rawPickupToDropMiles: number;
    urgency?: string | null;
    loadClass?: string | null;
    localRun?: boolean;
  },
  rates: HotshotRates,
): number {
  const rate = ratePerMileFromRates(rates, input.urgency);
  const equipmentUsd = equipmentFromRates(rates, input.loadClass);
  const originLeg =
    input.rawOriginToPickupMiles != null && Number.isFinite(input.rawOriginToPickupMiles)
      ? input.rawOriginToPickupMiles
      : null;
  const billedMiles = hotshotBilledMiles((originLeg ?? 0) + input.rawPickupToDropMiles) ?? 0;
  const mileageUsd = Math.round(billedMiles * rate * 100) / 100;
  const promo = Math.max(0, Math.min(50, Number(rates.promoPercentOff) || 0)) / 100;
  const mileageAfterPromo = Math.round(mileageUsd * (1 - promo) * 100) / 100;
  const floor = input.localRun ? rates.localMinTripUsd : rates.minTripUsd;
  const totalUsd = Math.max(floor, mileageAfterPromo) + equipmentUsd;
  return Math.min(HOTSHOT_PRICING.maxQuoteUsd, Math.round(totalUsd * 100) / 100);
}

export function hotshotTripUsd(input: {
  rawOriginToPickupMiles?: number | null;
  rawPickupToDropMiles: number;
  urgency?: string | null;
  loadClass?: string | null;
  /**
   * Both ends inside a service town — see hotshotIsLocalRun. Picks the FLOOR
   * ($75 vs $300); it no longer changes how the miles are billed, because
   * every truck drives home.
   */
  localRun?: boolean;
}): number {
  return hotshotTripUsdWithRates(input, PLATFORM_HOTSHOT_RATES);
}

// ─── Server-side recompute ───────────────────────────────────────────────────

/**
 * Structured pricing inputs the hubs send alongside `amountCents`. These are the
 * same coordinates already carried on the request for dispatch, so they cost
 * nothing extra to trust-check.
 */
export type GuestQuoteInputs = {
  serviceLine?: string | null;
  /** Vendor / store origin for the store-pickup lines. */
  originLat?: number | null;
  originLng?: number | null;
  /** Drop for every line. */
  dropLat?: number | null;
  dropLng?: number | null;
  /** Hotshots only. */
  pickupLat?: number | null;
  pickupLng?: number | null;
  gpsLat?: number | null;
  gpsLng?: number | null;
  urgency?: string | null;
  loadClass?: string | null;
  /** Hotshots: the ready time sets a floor on which urgency tier is honest. */
  readyDateIso?: string | null;
  readyWindowId?: string | null;
  /** Semi wide-load (lowboy) — not hotshot decks. */
  trailerKind?: string | null;
  widthFt?: number | null;
  heightFt?: number | null;
  permitBy?: string | null;
  /** Grocery optional tip, added only when local is payable now. */
  tipUsd?: number | null;
  /** Pin-the-work compass heading for the Operator Desk Map globe. */
  orientation?: string | null;
};

/**
 * What tier the ready time IMPLIES — advisory, not enforced.
 *
 * Standard's guarantee is "rolls within 24 hrs of ready"; Rush is the same-day
 * tier. So a ready date of today means Standard's promise doesn't describe the
 * run, and the hub says so in a note. It does not re-price the order and it
 * does not block checkout: the customer picks the tier they want to pay for,
 * and the bid board — where the tier sets driver pay per mile — decides how
 * fast it actually moves. Callers must not use this to adjust money.
 *
 * The clock does NOT push anyone to Critical: oilfield hotshot runs 24/7, so a
 * 2am pad delivery is an ordinary Rush, not a premium. Critical is a dispatch
 * promise (rig-down / NPT, 30-min assign, direct run) the customer chooses —
 * it is never inferred from the hour. What the hour DOES constrain is the
 * pickup counter: see hotshotPickupClosedBlocked.
 *
 * `now` is injectable so this is testable.
 */
export function hotshotUrgencyFloor(
  readyDateIso: string | null | undefined,
  readyWindowId: string | null | undefined,
  now: Date,
): 'rush' | 'critical' | null {
  void readyWindowId;
  if (!readyDateIso) return null;
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  if (readyDateIso > today) return null;
  return 'rush';
}

/** Whole days from today's noon to the ready day's noon (DST-safe, like welder). */
export function hotshotLeadDays(readyDateIso: string | null | undefined, now: Date): number {
  if (!readyDateIso) return 99;
  const [y, m, d] = readyDateIso.split('-').map((n) => Number(n));
  if (!y || !m || !d) return 99;
  const readyNoon = new Date(y, m - 1, d, 12, 0, 0, 0).getTime();
  const todayNoon = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12, 0, 0, 0).getTime();
  return Math.round((readyNoon - todayNoon) / 86_400_000);
}

/**
 * The customer's chosen ready DAY drives the urgency tier (and thus $/mi):
 * today → Critical, tomorrow → Rush, 2+ days out → Standard. Replaces the
 * urgency chips — picking a sooner day IS choosing to pay the faster rate, the
 * same "sooner costs more" model welder/WM express through their calendars.
 */
export function hotshotUrgencyFromReadyDate(
  readyDateIso: string | null | undefined,
  now: Date,
): HotshotUrgencyId {
  const lead = hotshotLeadDays(readyDateIso, now);
  if (lead <= 0) return 'critical';
  if (lead === 1) return 'rush';
  return 'standard';
}

/**
 * Compact per-day calendar badge. Only the first two days carry a charge
 * label — today (Critical) and tomorrow (Rush); 2+ days out is base Standard
 * and shows NO badge, the welder/WM idiom: the calendar flags the premium
 * days, silence means the price has come down.
 */
export function hotshotReadyDayBadge(readyDateIso: string | null | undefined, now: Date): string {
  const lead = hotshotLeadDays(readyDateIso, now);
  if (lead <= 0) return `CRIT $${HOTSHOT_PRICING.ratePerMileUsd.critical.toFixed(2)}`;
  if (lead === 1) return `RUSH $${HOTSHOT_PRICING.ratePerMileUsd.rush.toFixed(2)}`;
  return '';
}

/**
 * Whether the pickup counter LOOKS closed for an ASAP run — advisory only.
 *
 * Pads and facilities are 24/7, so this only applies to a business pickup
 * (parts house, supply yard, store) that Google reports as closed right now
 * when the customer wants it picked up NOW. A scheduled ready time is the
 * customer's call — we show the hours instead of guessing across a week.
 *
 * This is shown, never enforced. Google's hours go stale, and its isOpen()
 * returns undefined whenever utc_offset_minutes wasn't fetched — which is how
 * an open Home Depot reported "closed" and blocked a real checkout.
 */
export function hotshotPickupClosedBlocked(input: {
  pickupKind?: string | null;
  pickupOpenNow?: boolean | null;
  readyWindowId?: string | null;
}): boolean {
  if (input.pickupKind === 'oilfield') return false;
  if (input.pickupOpenNow !== false) return false;
  return input.readyWindowId === 'ready now';
}

export function usdToCents(usd: number | null | undefined): number | null {
  if (usd == null || !Number.isFinite(usd) || usd < 1) return null;
  return Math.round(usd * 100);
}

/**
 * Recompute what the quote should have been. Returns null when the inputs are
 * absent or insufficient — callers must treat null as "cannot judge", never as
 * "free" or "invalid".
 */
export function expectedGuestQuoteCents(
  inputs: GuestQuoteInputs | null | undefined,
): number | null {
  if (!inputs) return null;
  const line = String(inputs.serviceLine || '');
  const dropLat = finiteCoord(inputs.dropLat);
  const dropLng = finiteCoord(inputs.dropLng);
  if (dropLat == null || dropLng == null) return null;

  if (line === 'grocery' || line === 'tacos') {
    const originLat = finiteCoord(inputs.originLat);
    const originLng = finiteCoord(inputs.originLng);
    if (originLat == null || originLng == null) return null;
    const miles = haversineMiles(originLat, originLng, dropLat, dropLng);
    if (miles == null) return null;
    if (line === 'grocery') {
      const g = groceryRequestQuote(miles);
      if (g.payableNowUsd == null) return null;
      return usdToCents(g.payableNowUsd + groceryTipUsd((inputs as { tipUsd?: unknown }).tipUsd));
    }
    return usdToCents(storePickupDeliveryUsd(miles, FOOD_PRICING));
  }

  if (line === 'hotshots') {
    // Price the tier the customer chose, from the SAME coordinates (never a
    // client-declared "this was local" discount). Platform rates here; a card
    // booking prices via expectedHotshotCentsWithRates(inputs, card).
    return expectedHotshotCentsWithRates(inputs, PLATFORM_HOTSHOT_RATES);
  }

  if (line === 'semis') {
    const pickupLat = finiteCoord(inputs.pickupLat);
    const pickupLng = finiteCoord(inputs.pickupLng);
    if (pickupLat == null || pickupLng == null) return null;
    const q = estimateSemiWideQuote({
      pickupLat,
      pickupLng,
      dropLat,
      dropLng,
      trailerKind: inputs.trailerKind,
      widthFt: inputs.widthFt,
      heightFt: inputs.heightFt,
      permitBy: inputs.permitBy,
      readyDateIso: inputs.readyDateIso,
    });
    return q.quoteReady ? q.totalCents : null;
  }

  // water / waste_management run their own checkout stacks — not judged here.
  return null;
}

/**
 * The hotshot amount recomputed SERVER-SIDE from the request coordinates and a
 * given rate source (platform or a hauler's card). The list endpoint and the
 * checkout override both call this, so a booked price == the audit == the client
 * quote. Returns null when coords are insufficient (never treat null as free).
 */
export function expectedHotshotCentsWithRates(
  inputs: GuestQuoteInputs | null | undefined,
  rates: HotshotRates,
): number | null {
  if (!inputs) return null;
  const dropLat = finiteCoord(inputs.dropLat);
  const dropLng = finiteCoord(inputs.dropLng);
  const pickupLat = finiteCoord(inputs.pickupLat);
  const pickupLng = finiteCoord(inputs.pickupLng);
  if (dropLat == null || dropLng == null || pickupLat == null || pickupLng == null) return null;
  const pickupToDrop = haversineMiles(pickupLat, pickupLng, dropLat, dropLng);
  if (pickupToDrop == null) return null;
  const gpsLat = finiteCoord(inputs.gpsLat);
  const gpsLng = finiteCoord(inputs.gpsLng);
  const originToPickup =
    gpsLat != null && gpsLng != null
      ? haversineMiles(gpsLat, gpsLng, pickupLat, pickupLng)
      : null;
  return usdToCents(
    hotshotTripUsdWithRates(
      {
        rawOriginToPickupMiles: originToPickup,
        rawPickupToDropMiles: pickupToDrop,
        urgency: inputs.urgency,
        loadClass: inputs.loadClass,
        localRun: hotshotIsLocalRun({ pickupLat, pickupLng, dropLat, dropLng }),
      },
      rates,
    ),
  );
}

/** Rounding slack before a difference is worth recording. */
export const GUEST_QUOTE_TOLERANCE_CENTS = 1;

export type GuestQuoteAudit = {
  expectedCents: number | null;
  chargedCents: number;
  deltaCents: number | null;
  /** true when we could compute an expectation AND it differs beyond tolerance */
  mismatch: boolean;
};

export function auditGuestQuote(
  chargedCents: number,
  inputs: GuestQuoteInputs | null | undefined,
  rates?: HotshotRates | null,
): GuestQuoteAudit {
  const expectedCents = rates
    ? expectedHotshotCentsWithRates(inputs, rates)
    : expectedGuestQuoteCents(inputs);
  if (expectedCents == null) {
    return { expectedCents: null, chargedCents, deltaCents: null, mismatch: false };
  }
  const deltaCents = chargedCents - expectedCents;
  return {
    expectedCents,
    chargedCents,
    deltaCents,
    mismatch: Math.abs(deltaCents) > GUEST_QUOTE_TOLERANCE_CENTS,
  };
}
